import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    Button,
    Card,
    CardContent,
    FormControlLabel,
    Checkbox,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Box,
    Chip,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import useStyles from './OrderPageMobile.styles';
import { getOrderFilters } from '../../services/orders';

const FilterModal = ({
    open,
    storeId,
    onClose,
    onApplyFilters,
    onClearFilters,
    currentFilters = {},
}) => {
    const classes = useStyles();

    const [selectedDateChip, setSelectedDateChip] = useState(currentFilters.datePreset || '');
    const [dateFrom, setDateFrom] = useState(currentFilters.dateFrom || '');
    const [dateTo, setDateTo] = useState(currentFilters.dateTo || '');
    const [deliveryStatus, setDeliveryStatus] = useState(currentFilters.deliveryStatus || []);
    const [orderStatus, setOrderStatus] = useState(currentFilters.orderStatus || []);
    const [salesChannel, setSalesChannel] = useState(currentFilters.salesChannel || []);
    const [selectedBranch, setSelectedBranch] = useState(
        currentFilters.branch || 'Todas las sucursales'
    );
    const [branches, setBranches] = useState([]);
    const [dataOrder, setDataOrder] = useState([]);
    const [dataDelivery, setDataDelivery] = useState([]);

    useEffect(() => {
        if (currentFilters) {
            setSelectedDateChip(currentFilters.datePreset || '');
            setDateFrom(currentFilters.dateFrom || '');
            setDateTo(currentFilters.dateTo || '');
            setDeliveryStatus(currentFilters.deliveryStatus || []);
            setOrderStatus(currentFilters.orderStatus || []);
            setSalesChannel(currentFilters.salesChannel || []);
            setSelectedBranch(currentFilters.branch || 'Todas las sucursales');
        }
    }, [currentFilters]);

    const dateChips = ['Hoy', 'Ayer', '7 días', '30 días'];

    const handleDateChipClick = (chip) => {
        setSelectedDateChip(chip);
        const fromDate = new Date();
        const toDate = new Date();

        switch (chip) {
            case 'Ayer':
                fromDate.setDate(fromDate.getDate() - 1);
                toDate.setDate(toDate.getDate() - 1);
                break;
            case '7 días':
                fromDate.setDate(fromDate.getDate() - 7);
                break;
            case '30 días':
                fromDate.setDate(fromDate.getDate() - 30);
                break;
            default:
                break;
        }

        const formatDateForInput = (date) => {
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        setDateFrom(formatDateForInput(fromDate));
        setDateTo(formatDateForInput(toDate));
    };

    const handleOrderStatusChange = (item) => {
        setOrderStatus((prev) => {
            const exists = prev.some((status) => status.status_id === item.status_id);
            return exists
                ? prev.filter((status) => status.status_id !== item.status_id)
                : [...prev, item];
        });
    };

    const handleDeliveryStatusChange = (item) => {
        setDeliveryStatus((prev) => {
            const exists = prev.some((status) => status.status_id === item.status_id);
            return exists
                ? prev.filter((status) => status.status_id !== item.status_id)
                : [...prev, item];
        });
    };

    const handleClear = () => {
        setSelectedDateChip('');
        setDateFrom('');
        setDateTo('');
        setDeliveryStatus([]);
        setOrderStatus([]);
        setSalesChannel([]);
        setSelectedBranch('Todas las sucursales');

        onClearFilters && onClearFilters();
        onClose();
    };

    const convertFiltersForBackend = (
        customOrderStatus,
        customDeliveryStatus,
        customBranch,
        customDatePreset,
        customDateFrom,
        customDateTo
    ) => {
        const currentOrderStatus =
            customOrderStatus !== undefined ? customOrderStatus : orderStatus;
        const currentDeliveryStatus =
            customDeliveryStatus !== undefined ? customDeliveryStatus : deliveryStatus;
        const currentBranch = customBranch !== undefined ? customBranch : selectedBranch;
        const currentDatePreset =
            customDatePreset !== undefined ? customDatePreset : selectedDateChip;
        const currentDateFrom = customDateFrom !== undefined ? customDateFrom : dateFrom;
        const currentDateTo = customDateTo !== undefined ? customDateTo : dateTo;

        return {
            datePreset: currentDatePreset,
            dateFrom: currentDateFrom,
            dateTo: currentDateTo,
            branch: currentBranch !== 'Todas las sucursales' ? currentBranch : '',
            orderStatus: currentOrderStatus,
            deliveryStatus: currentDeliveryStatus,
            salesChannelState: salesChannel,
        };
    };

    const handleApply = () => {
        const backendFilters = convertFiltersForBackend();
        onApplyFilters && onApplyFilters(backendFilters);
        onClose();
    };

    useEffect(() => {
        if (storeId) {
            getOrderFilters(storeId)
                .then((res) => {
                    setBranches(res?.data?.user_stores || []);
                    setDataOrder(res?.data?.order_status_counter || []);
                    setDataDelivery(res?.data?.order_shipping_counter || []);
                })
                .catch((err) => {
                    console.error('Error fetching filters:', err);
                });
        }
    }, [storeId]);

    const isOrderStatusSelected = (item) =>
        orderStatus.some((status) => status.status_id === item.status_id);
    const isDeliveryStatusSelected = (item) =>
        deliveryStatus.some((status) => status.status_id === item.status_id);

    return (
        <Dialog open={open} onClose={onClose} fullScreen className={classes.dialog}>
            <DialogTitle className={classes.dialogTitle} disableTypography>
                <Typography className={classes.title}>Filtros</Typography>
                <IconButton className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                <Card className={classes.filterCard}>
                    <CardContent className={classes.cardContent}>
                        <Typography className={classes.cardTitle}>Fecha</Typography>
                        <Box className={classes.dateChipsContainer}>
                            {dateChips.map((chip) => (
                                <Chip
                                    key={chip}
                                    label={chip}
                                    onClick={() => handleDateChipClick(chip)}
                                    className={`${classes.dateChip} ${
                                        selectedDateChip === chip ? 'selected' : ''
                                    }`}
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                        <Box className={classes.dateInputsContainer}>
                            <TextField
                                label="Desde"
                                type="date"
                                value={dateFrom}
                                onChange={(e) => {
                                    setDateFrom(e.target.value);
                                    setSelectedDateChip('');
                                }}
                                className={classes.textField}
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Hasta"
                                type="date"
                                value={dateTo}
                                onChange={(e) => {
                                    setDateTo(e.target.value);
                                    setSelectedDateChip('');
                                }}
                                className={classes.textField}
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>
                    </CardContent>
                </Card>

                <Card className={classes.filterCard}>
                    <CardContent className={classes.cardContent}>
                        <Typography className={classes.cardTitle}>Estado del pedido</Typography>
                        <Box className={classes.checkboxContainer}>
                            {dataOrder.map((item) => (
                                <FormControlLabel
                                    key={item.status_id}
                                    control={
                                        <Checkbox
                                            checked={isOrderStatusSelected(item)}
                                            onChange={() => handleOrderStatusChange(item)}
                                        />
                                    }
                                    label={`${item.status_name} (${item.quantity})`}
                                    className={classes.checkboxLabel}
                                />
                            ))}
                        </Box>
                    </CardContent>
                </Card>

                <Card className={classes.filterCard}>
                    <CardContent className={classes.cardContent}>
                        <Typography className={classes.cardTitle}>Estado del envío</Typography>
                        <Box className={classes.checkboxContainer}>
                            {dataDelivery.map((item) => (
                                <FormControlLabel
                                    key={item.status_id}
                                    control={
                                        <Checkbox
                                            checked={isDeliveryStatusSelected(item)}
                                            onChange={() => handleDeliveryStatusChange(item)}
                                        />
                                    }
                                    label={`${item.status_name} (${item.quantity})`}
                                    className={classes.checkboxLabel}
                                />
                            ))}
                        </Box>
                    </CardContent>
                </Card>

                <Card className={classes.filterCard}>
                    <CardContent className={classes.cardContent}>
                        <Typography className={classes.cardTitle}>Sucursal</Typography>
                        <FormControl variant="outlined" fullWidth className={classes.selectField}>
                            <Select
                                value={selectedBranch}
                                onChange={(e) => setSelectedBranch(e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="Todas las sucursales">
                                    Todas las sucursales
                                </MenuItem>
                                {branches.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </CardContent>
                </Card>
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                <Button onClick={handleClear} className={classes.clearButton} variant="outlined">
                    Limpiar
                </Button>
                <Button onClick={handleApply} className={classes.applyButton} variant="contained">
                    Aplicar filtros
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FilterModal;
